/**
 * Common utility functions for telemetry tracking
 * Provides reusable helpers for element selection, state detection,
 * and other common telemetry operations.
 */

/**
 * Generate a CSS selector string for an element
 * Useful for identifying elements in telemetry events
 *
 * Priority order:
 * 1. ID if available
 * 2. Tag name with up to 2 class names
 * 3. Tag name only
 *
 * @param element - The DOM element to generate a selector for
 * @returns A CSS selector string representing the element
 *
 * @example
 * getElementSelector(document.getElementById('myId')) // Returns: "#myId"
 * getElementSelector(document.querySelector('.class1.class2')) // Returns: "div.class1.class2"
 */
export function getElementSelector(element: Element): string {
  if (element.id) {
    return `#${element.id}`;
  }

  if (element.className && typeof element.className === 'string') {
    const classes = element.className
      .split(' ')
      .filter((c) => c)
      .slice(0, 2)
      .join('.');
    return `${element.tagName.toLowerCase()}${classes ? '.' + classes : ''}`;
  }

  return element.tagName.toLowerCase();
}

/**
 * Get the current document load state
 * Maps document.readyState to a more specific telemetry-friendly format
 *
 * @returns The current load state of the document
 *
 * @example
 * getLoadState() // Returns: 'complete' | 'loading' | 'dom_interactive'
 */
export function getLoadState(): 'loading' | 'dom_interactive' | 'dom_content_loaded' | 'complete' {
  if (typeof document === 'undefined') return 'complete';

  switch (document.readyState) {
    case 'loading':
      return 'loading';
    case 'interactive':
      return 'dom_interactive';
    case 'complete':
      return 'complete';
    default:
      return 'complete';
  }
}

/**
 * Safe feature detection helper
 * Checks if a nested property exists on the window object
 *
 * @param featurePath - Dot-separated path to the feature (e.g., 'navigator.serviceWorker')
 * @returns True if the feature exists, false otherwise
 *
 * @example
 * hasFeature('navigator.serviceWorker') // Returns: true or false
 * hasFeature('performance.measure') // Returns: true or false
 */
export function hasFeature(featurePath: string): boolean {
  const parts = featurePath.split('.');
  let current: unknown = window;

  for (const part of parts) {
    if (typeof current !== 'object' || current === null) return false;
    // eslint-disable-next-line security/detect-object-injection
    current = (current as Record<string, unknown>)[part];
    if (current === undefined) return false;
  }

  return current !== undefined;
}

/**
 * Generate a unique session ID
 * Combines timestamp with random string for uniqueness
 *
 * @returns A unique session identifier string
 *
 * @example
 * generateSessionId() // Returns: "1704067200000-a1b2c3d4e"
 */
export function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Safely parse a user agent string to extract browser/OS info
 * Provides a simple parsing mechanism without heavy dependencies
 *
 * @param userAgent - The user agent string to parse (defaults to navigator.userAgent)
 * @returns An object containing parsed browser and OS information
 *
 * @example
 * parseUserAgent() // Returns: \{ browser: 'Chrome', version: '120.0', os: 'macOS' \}
 */
export function parseUserAgent(userAgent: string = navigator.userAgent): {
  browser: string;
  version: string;
  os: string;
} {
  // Simple UA parsing - can be enhanced as needed
  let browser = 'Unknown';
  let version = 'Unknown';
  let os = 'Unknown';

  // OS detection
  if (userAgent.includes('Win')) os = 'Windows';
  else if (userAgent.includes('Mac')) os = 'macOS';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';

  // Browser detection (order matters!)
  if (userAgent.includes('Edg/')) {
    browser = 'Edge';
    version = userAgent.match(/Edg\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg')) {
    browser = 'Chrome';
    version = userAgent.match(/Chrome\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
    version = userAgent.match(/Version\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Firefox/')) {
    browser = 'Firefox';
    version = userAgent.match(/Firefox\/([\d.]+)/)?.[1] || 'Unknown';
  }

  return { browser, version, os };
}

/**
 * Throttle function execution
 * Ensures a function is not called more than once in a specified time period
 *
 * @param func - The function to throttle
 * @param wait - The minimum time between function calls in milliseconds
 * @returns A throttled version of the function
 *
 * @example
 * const throttledHandler = throttle(() =\> console.log('Called'), 1000);
 * window.addEventListener('scroll', throttledHandler);
 */
export function throttle<T extends (...args: unknown[]) => void>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastRan = 0;

  return function (this: unknown, ...args: Parameters<T>) {
    const now = Date.now();

    if (!lastRan || now - lastRan >= wait) {
      func.apply(this, args);
      lastRan = now;
    } else {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(
        () => {
          func.apply(this, args);
          lastRan = Date.now();
        },
        wait - (now - lastRan)
      );
    }
  } as T;
}
